// src/screens/admin/AdminDictionnaireRayonsScreen.jsx
//
// Dictionnaire des RAYONS (fichier « <TRIG>_dictionnaire_rayons.xlsx » sur le
// partage « collecteur »). Deux usages :
//   1) Éditer le fichier (GISM1 / libellé / métrage) et l'enregistrer ;
//   2) Générer des étiquettes A8 (QR ou code-barres) découpées en SOUS-ZONES :
//      métrage N → N sous-zones « <GISM1>_A, _B, … » (toujours au moins « _A »).
//      Filtrables par emplacement (tous / MAGASIN / DOCK).
// Société lue depuis la sélection GLOBALE (Header).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  HiTag,
  HiOfficeBuilding,
  HiPlus,
  HiTrash,
  HiSave,
  HiRefresh,
  HiUpload,
  HiDownload,
  HiSearch,
  HiEye,
  HiEyeOff,
  HiX,
} from "react-icons/hi";
import { useSelector } from "react-redux";
import { useGetMyEntreprisesQuery } from "../../slices/entrepriseApiSlice";
import { selectGlobalEntrepriseId } from "../../slices/entrepriseGlobalSlice";
import { BASE_URL } from "../../constants";
import "./AdminDictionnaireRayonsScreen.css";

// Nb de sous-zones d'un métrage (toujours au moins 1 → « _A »).
const nbSousZones = (metrage) => Math.max(1, Math.round(Number(metrage) || 0));

// Suffixe de sous-zone bijectif base 26 : 0→A, 25→Z, 26→AA (miroir du backend).
const letterSuffix = (n) => {
  let s = "";
  let x = Math.max(0, Math.floor(n)) + 1;
  while (x > 0) {
    const r = (x - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
};

// Codes des sous-zones d'une zone (ex. au_M / 8 → au_M_A … au_M_H).
const subZoneCodes = (gism1, metrage) => {
  const code = String(gism1 || "").trim();
  if (!code) return [];
  const n = nbSousZones(metrage);
  return Array.from({ length: n }, (_, i) => `${code}_${letterSuffix(i)}`);
};

const AdminDictionnaireRayonsScreen = () => {
  const { data: entreprises, isLoading: loadingEntreprises } =
    useGetMyEntreprisesQuery();

  const globalEntrepriseId = useSelector(selectGlobalEntrepriseId);
  const entrepriseData = entreprises?.find((e) => e._id === globalEntrepriseId);
  const nomDossierDBF = entrepriseData?.nomDossierDBF;

  const idRef = useRef(1);
  const withId = (r) => ({ _id: idRef.current++, ...r });

  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [fichier, setFichier] = useState("");
  const [exists, setExists] = useState(true);
  const [codeType, setCodeType] = useState("barcode"); // "barcode" | "qr"
  // Étiquettes : true = une par SOUS-ZONE (<GISM1>_A…), false = une par ZONE
  // (code GISM1 seul, sans suffixe _A/_B).
  const [avecSousZones, setAvecSousZones] = useState(true);
  // Étiquettes : emplacement à imprimer (TOUS | MAGASIN | DOCK).
  const [emplEtiquettes, setEmplEtiquettes] = useState("TOUS");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [importing, setImporting] = useState(false);
  // Mode de l'import Excel : false = fusion (défaut), true = remplacement total.
  const [remplacerImport, setRemplacerImport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sepCsv, setSepCsv] = useState(";"); // séparateur de l'export correction GISM
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);

  // Recherche / filtres / affichage (UX pour longs dictionnaires, ex. QC)
  const [search, setSearch] = useState("");
  const [emplFiltre, setEmplFiltre] = useState("TOUS"); // TOUS | MAGASIN | DOCK
  const [showSubzones, setShowSubzones] = useState(false); // sous-zones repliées par défaut

  const fileInputRef = useRef(null);

  const erreurReponse = async (res, base) => {
    let msg = `${base} (${res.status})`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch {
      /* non-JSON */
    }
    return new Error(msg);
  };

  // ── Chargement du dictionnaire ───────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!nomDossierDBF) return;
    setError("");
    setInfo(null);
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}`,
        { credentials: "include" },
      );
      if (!res.ok) throw await erreurReponse(res, "Lecture échouée");
      const data = await res.json();
      idRef.current = 1;
      setRows((data.rows || []).map(withId));
      setFichier(data.fichier || "");
      setExists(!!data.exists);
      setDirty(false);
      if (data.justMaterialized) {
        setInfo("Sous-zones ajoutées au fichier (matérialisation initiale).");
      }
    } catch (e) {
      setError(e.message || "Impossible de lire le dictionnaire.");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomDossierDBF]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Édition ──────────────────────────────────────────────────────────────────
  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [field]: value } : r)),
    );
    setDirty(true);
  };
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      withId({ gism1: "", libelle: "", metrage: 0, priorite: "", emplacement: "MAGASIN" }),
    ]);
    setDirty(true);
  };
  const deleteRow = (id) => {
    setRows((prev) => prev.filter((r) => r._id !== id));
    setDirty(true);
  };

  const totalSousZones = useMemo(
    () =>
      rows.reduce(
        (n, r) => n + (String(r.gism1).trim() ? nbSousZones(r.metrage) : 0),
        0,
      ),
    [rows],
  );
  const nbCodes = rows.filter((r) => String(r.gism1).trim()).length;

  // Lignes retenues pour les ÉTIQUETTES (selon l'emplacement choisi).
  const rowsEtiquettes = useMemo(
    () =>
      emplEtiquettes === "TOUS"
        ? rows
        : rows.filter(
            (r) =>
              (r.emplacement === "DOCK" ? "DOCK" : "MAGASIN") === emplEtiquettes,
          ),
    [rows, emplEtiquettes],
  );
  const nbCodesEtiq = rowsEtiquettes.filter((r) => String(r.gism1).trim()).length;
  const totalSousZonesEtiq = useMemo(
    () =>
      rowsEtiquettes.reduce(
        (n, r) => n + (String(r.gism1).trim() ? nbSousZones(r.metrage) : 0),
        0,
      ),
    [rowsEtiquettes],
  );

  // ── Recherche + filtre emplacement (affichage seulement, édition via _id) ─────
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (emplFiltre !== "TOUS") {
        const empl = r.emplacement === "DOCK" ? "DOCK" : "MAGASIN";
        if (empl !== emplFiltre) return false;
      }
      if (!q) return true;
      const code = String(r.gism1 || "").toLowerCase();
      const lib = String(r.libelle || "").toLowerCase();
      return code.includes(q) || lib.includes(q);
    });
  }, [rows, search, emplFiltre]);

  const filtreActif = search.trim() !== "" || emplFiltre !== "TOUS";

  // ── Enregistrement ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!nomDossierDBF) return setError("Sélectionnez une société dans l'en-tête.");
    setError("");
    setInfo(null);
    setSaving(true);
    try {
      const payload = {
        rows: rows.map((r) => {
          const prioStr = String(r.priorite ?? "").trim();
          return {
            gism1: String(r.gism1 || "").trim(),
            libelle: String(r.libelle || "").trim(),
            metrage: Math.max(0, Math.round(Number(r.metrage) || 0)),
            priorite: prioStr === "" ? null : Math.max(0, Math.round(Number(prioStr) || 0)),
            emplacement: r.emplacement === "DOCK" ? "DOCK" : "MAGASIN",
          };
        }),
      };
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) throw await erreurReponse(res, "Enregistrement échoué");
      const data = await res.json();
      setDirty(false);
      setExists(true);
      setInfo(data.message || "Dictionnaire enregistré.");
      load();
    } catch (e) {
      setError(e.message || "Impossible d'enregistrer.");
    } finally {
      setSaving(false);
    }
  };

  // ── Génération des étiquettes (à partir du fichier ENREGISTRÉ) ───────────────
  const generer = async () => {
    if (!nomDossierDBF) return setError("Sélectionnez une société dans l'en-tête.");
    setError("");
    setInfo(null);
    setGenerating(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}/etiquettes`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            codeType,
            sousZones: avecSousZones,
            // TOUS → pas de filtre côté serveur
            ...(emplEtiquettes !== "TOUS" && { emplacement: emplEtiquettes }),
          }),
        },
      );
      if (!res.ok) throw await erreurReponse(res, "Génération échouée");
      const nb = res.headers.get("X-Etiquettes");
      const tronque = res.headers.get("X-Tronque") === "1";
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `etiquettes_rayons_${codeType}${
        emplEtiquettes !== "TOUS" ? `_${emplEtiquettes.toLowerCase()}` : ""
      }${avecSousZones ? "" : "_zones"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
      setInfo(
        `${nb ?? "?"} étiquette(s) générée(s) au format A8` +
          (tronque ? " (tronqué à 5000)." : "."),
      );
    } catch (e) {
      setError(e.message || "Impossible de générer les étiquettes.");
    } finally {
      setGenerating(false);
    }
  };

  // ── Import de masse depuis un Excel (fusion ou remplacement) ─────────────────
  const onPickImport = () => fileInputRef.current?.click();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de réimporter le même fichier
    if (!file) return;
    if (!nomDossierDBF) return setError("Sélectionnez une société dans l'en-tête.");
    // Le remplacement supprime des rayons : on confirme explicitement.
    if (
      remplacerImport &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Remplacer TOUT le dictionnaire par « ${file.name} » ?\n\n` +
          `Les rayons actuellement présents qui ne figurent pas dans le fichier ` +
          `seront SUPPRIMÉS du dictionnaire.`,
      )
    ) {
      return;
    }
    setError("");
    setInfo(null);
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("remplacer", remplacerImport ? "1" : "0");
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}/import`,
        { method: "POST", credentials: "include", body: form },
      );
      if (!res.ok) throw await erreurReponse(res, "Import échoué");
      const data = await res.json();
      setInfo(data.message || "Import effectué.");
      setExists(true);
      load(); // recharge le dictionnaire mis à jour
    } catch (err) {
      setError(err.message || "Impossible d'importer le fichier.");
    } finally {
      setImporting(false);
    }
  };

  // ── Téléchargement d'une copie du fichier dictionnaire ───────────────────────
  const telecharger = async () => {
    if (!nomDossierDBF) return setError("Sélectionnez une société dans l'en-tête.");
    setError("");
    setInfo(null);
    setDownloading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}/fichier`,
        { credentials: "include" },
      );
      if (!res.ok) throw await erreurReponse(res, "Téléchargement échoué");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `dictionnaire_rayons_${nomDossierDBF}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (err) {
      setError(err.message || "Impossible de télécharger le fichier.");
    } finally {
      setDownloading(false);
    }
  };

  // ── Téléchargement des RAYONS SEULS (sans sous-zones), selon l'emplacement ────
  const telechargerRayons = async () => {
    if (!nomDossierDBF) return setError("Sélectionnez une société dans l'en-tête.");
    setError("");
    setInfo(null);
    setDownloading(true);
    try {
      const empl = emplFiltre && emplFiltre !== "TOUS" ? emplFiltre : "";
      const q = empl ? `?emplacement=${encodeURIComponent(empl)}` : "";
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}/rayons${q}`,
        { credentials: "include" },
      );
      if (!res.ok) throw await erreurReponse(res, "Téléchargement échoué");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `rayons${empl ? `_${empl}` : ""}_${nomDossierDBF}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (err) {
      setError(err.message || "Impossible de télécharger les rayons.");
    } finally {
      setDownloading(false);
    }
  };

  // ── Export CSV de correction des gisements de la base article ────────────────
  // Une ligne par article (NART;GISM1;GISM2) : le dictionnaire décide la colonne
  // (code MAGASIN → GISM1, code DOCK → GISM2, inconnu/ambigu → vide). Fichier
  // destiné au RÉIMPORT dans Stock XL pour corriger les gisements.
  const telechargerCorrectionGism = async () => {
    if (!nomDossierDBF) return setError("Sélectionnez une société dans l'en-tête.");
    setError("");
    setInfo(null);
    setDownloading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/dictionnaire-rayons/${nomDossierDBF}/export-gism?sep=${encodeURIComponent(
          sepCsv === "\t" ? "tab" : sepCsv,
        )}`,
        { credentials: "include" },
      );
      if (!res.ok) throw await erreurReponse(res, "Export échoué");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `correction_gism_${nomDossierDBF}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (err) {
      setError(err.message || "Impossible d'exporter le fichier de correction.");
    } finally {
      setDownloading(false);
    }
  };

  if (loadingEntreprises) {
    return (
      <div className="dicr-screen">
        <div className="dicr-placeholder">Chargement des entreprises…</div>
      </div>
    );
  }

  return (
    <div className="dicr-screen">
      <div className="dicr-header">
        <h1>
          <HiTag /> Dictionnaire des rayons
        </h1>
        <p>
          Éditez le fichier des rayons (code GISM1 + libellé + métrage +
          priorité + emplacement), puis générez les étiquettes A8 en QR ou
          code-barres. La <strong>priorité</strong> définit l'ordre de
          préparation des commandes ; l'<strong>emplacement</strong> vaut{" "}
          <code>MAGASIN</code> (défaut) ou <code>DOCK</code>. Le métrage découpe
          chaque rayon en sous-zones (<code>_A</code>, <code>_B</code>…) : un
          rayon de métrage 8 donne 8 sous-zones, un rayon de métrage 0 en donne 1
          (<code>_A</code>). Vous pouvez aussi <strong>importer</strong> un Excel
          — en <em>fusion</em> (mise à jour + ajout, les rayons absents du fichier
          sont conservés) ou en <em>remplacement</em> si vous cochez «&nbsp;Remplacer
          tout le dictionnaire&nbsp;» (le fichier devient le dictionnaire complet) —
          ou <strong>télécharger</strong> une copie du fichier. Un rayon est
          identifié par son GISM1 <strong>et son emplacement</strong> : le même code
          peut exister en <code>MAGASIN</code> et en <code>DOCK</code>.
        </p>
      </div>

      {!nomDossierDBF && (
        <div className="dicr-card">
          <p className="dicr-hint">
            <HiOfficeBuilding /> Sélectionnez une société dans l'en-tête pour
            continuer.
          </p>
        </div>
      )}

      {nomDossierDBF && (
        <>
          {fichier && (
            <div className="dicr-filepath" title={fichier}>
              Fichier : <code>{fichier}</code>
              {!exists && <span className="dicr-badge-new"> (sera créé)</span>}
            </div>
          )}

          {error && <div className="dicr-error">{error}</div>}
          {info && <div className="dicr-info">{info}</div>}

          {/* ── Éditeur ─────────────────────────────────────────────── */}
          <div className="dicr-card">
            <div className="dicr-card-head">
              <label className="dicr-label">
                Rayons ({nbCodes}) · {totalSousZones} sous-zone(s)
                {filtreActif && (
                  <span className="dicr-count-filtered">
                    {" "}
                    — {visibleRows.length} affiché(s)
                  </span>
                )}
              </label>
              <div className="dicr-head-actions">
                <button
                  type="button"
                  className="dicr-btn"
                  onClick={load}
                  disabled={loading}
                  title="Recharger depuis le fichier"
                >
                  <HiRefresh /> {loading ? "…" : "Recharger"}
                </button>
                <button type="button" className="dicr-btn" onClick={addRow}>
                  <HiPlus /> Ajouter une ligne
                </button>
                <button
                  type="button"
                  className="dicr-btn"
                  onClick={onPickImport}
                  disabled={importing}
                  title={
                    remplacerImport
                      ? "Importer un Excel EN REMPLACEMENT : le fichier devient le dictionnaire, les rayons absents sont supprimés"
                      : "Importer un Excel (fusion : mise à jour + ajout par GISM1 + emplacement ; les rayons absents sont conservés)"
                  }
                >
                  <HiUpload />{" "}
                  {importing
                    ? "Import…"
                    : remplacerImport
                      ? "Importer (remplacer)"
                      : "Importer un Excel"}
                </button>
                <label
                  className="dicr-import-mode"
                  title="Coché : le fichier importé devient le dictionnaire complet — les rayons qui n'y figurent pas sont SUPPRIMÉS. Décoché : fusion, les rayons absents du fichier sont conservés."
                >
                  <input
                    type="checkbox"
                    checked={remplacerImport}
                    onChange={(e) => setRemplacerImport(e.target.checked)}
                    disabled={importing}
                  />
                  Remplacer tout le dictionnaire
                </label>
                <button
                  type="button"
                  className="dicr-btn"
                  onClick={telecharger}
                  disabled={downloading || !exists}
                  title="Télécharger une copie du fichier complet (avec sous-zones)"
                >
                  <HiDownload /> {downloading ? "…" : "Télécharger (complet)"}
                </button>
                <button
                  type="button"
                  className="dicr-btn"
                  onClick={telechargerRayons}
                  disabled={downloading || !exists}
                  title="Télécharger les rayons SANS sous-zones (selon l'emplacement sélectionné ci-dessous)"
                >
                  <HiDownload />{" "}
                  {downloading
                    ? "…"
                    : `Rayons seuls${
                        emplFiltre && emplFiltre !== "TOUS"
                          ? ` (${emplFiltre})`
                          : ""
                      }`}
                </button>
                <select
                  className="dicr-select"
                  value={sepCsv}
                  onChange={(e) => setSepCsv(e.target.value)}
                  disabled={downloading}
                  title="Séparateur du CSV de correction"
                >
                  <option value=";">Séparateur ;</option>
                  <option value=",">Séparateur ,</option>
                  <option value={"\t"}>Séparateur tabulation</option>
                  <option value="|">Séparateur |</option>
                </select>
                <button
                  type="button"
                  className="dicr-btn"
                  onClick={telechargerCorrectionGism}
                  disabled={downloading || !exists}
                  title="CSV de correction des gisements de la base article (une ligne par NART ; code MAGASIN → colonne GISM1, code DOCK → colonne GISM2 selon le dictionnaire, inconnu/ambigu → vide). À réimporter dans Stock XL."
                >
                  <HiDownload /> {downloading ? "…" : "Correction GISM (CSV)"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={onImportFile}
                />
              </div>
            </div>

            {/* ── Barre de recherche / filtres ─────────────────────────── */}
            <div className="dicr-filterbar">
              <div className="dicr-search">
                <HiSearch className="dicr-search-ic" />
                <input
                  className="dicr-search-in"
                  type="text"
                  value={search}
                  placeholder="Rechercher un code ou un libellé…"
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="dicr-search-clear"
                    onClick={() => setSearch("")}
                    title="Effacer la recherche"
                  >
                    <HiX />
                  </button>
                )}
              </div>

              <div className="dicr-empl-filter">
                <span className="dicr-filter-lbl">Emplacement :</span>
                {["TOUS", "MAGASIN", "DOCK"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`dicr-chip-btn ${emplFiltre === v ? "active" : ""}`}
                    onClick={() => setEmplFiltre(v)}
                  >
                    {v === "TOUS" ? "Tous" : v}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="dicr-btn dicr-btn-ghost"
                onClick={() => setShowSubzones((s) => !s)}
                title={
                  showSubzones
                    ? "Masquer les sous-zones sous chaque rayon"
                    : "Afficher les sous-zones sous chaque rayon"
                }
              >
                {showSubzones ? <HiEyeOff /> : <HiEye />}{" "}
                {showSubzones ? "Masquer les sous-zones" : "Afficher les sous-zones"}
              </button>
            </div>

            <div className="dicr-table-wrap">
              <table className="dicr-table">
                <thead>
                  <tr>
                    <th className="dicr-col-code">GISM1 (code)</th>
                    <th>Libellé</th>
                    <th className="dicr-col-metr">Métrage</th>
                    <th className="dicr-col-prio">Priorité</th>
                    <th className="dicr-col-empl">Emplacement</th>
                    <th className="dicr-col-sz">Sous-zones</th>
                    <th className="dicr-col-act"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="dicr-empty">
                        {loading
                          ? "Chargement…"
                          : "Aucune ligne. Cliquez « Ajouter une ligne »."}
                      </td>
                    </tr>
                  ) : visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="dicr-empty">
                        Aucun rayon ne correspond au filtre.{" "}
                        <button
                          type="button"
                          className="dicr-link-btn"
                          onClick={() => {
                            setSearch("");
                            setEmplFiltre("TOUS");
                          }}
                        >
                          Réinitialiser
                        </button>
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((r) => {
                      const hasCode = String(r.gism1).trim() !== "";
                      const sz = hasCode ? subZoneCodes(r.gism1, r.metrage) : [];
                      return (
                        <React.Fragment key={r._id}>
                          <tr className="dicr-zone-row">
                            <td>
                              <input
                                className="dicr-in mono"
                                value={r.gism1}
                                onChange={(e) =>
                                  updateRow(r._id, "gism1", e.target.value)
                                }
                                placeholder="ex : au_M"
                              />
                            </td>
                            <td>
                              <input
                                className="dicr-in"
                                value={r.libelle}
                                onChange={(e) =>
                                  updateRow(r._id, "libelle", e.target.value)
                                }
                                placeholder="ex : AU M precompte"
                              />
                            </td>
                            <td>
                              <input
                                className="dicr-in dicr-in-num"
                                type="number"
                                min="0"
                                step="1"
                                value={r.metrage}
                                onChange={(e) =>
                                  updateRow(r._id, "metrage", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="dicr-in dicr-in-num"
                                type="number"
                                min="0"
                                step="1"
                                value={r.priorite ?? ""}
                                placeholder="—"
                                title="Ordre de préparation des commandes (1 = en premier)"
                                onChange={(e) =>
                                  updateRow(r._id, "priorite", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <select
                                className="dicr-in dicr-select"
                                value={r.emplacement === "DOCK" ? "DOCK" : "MAGASIN"}
                                onChange={(e) =>
                                  updateRow(r._id, "emplacement", e.target.value)
                                }
                              >
                                <option value="MAGASIN">MAGASIN</option>
                                <option value="DOCK">DOCK</option>
                              </select>
                            </td>
                            <td className="dicr-sz-cell">
                              {hasCode ? sz.length : "—"}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="dicr-del"
                                onClick={() => deleteRow(r._id)}
                                title="Supprimer la ligne"
                              >
                                <HiTrash />
                              </button>
                            </td>
                          </tr>
                          {hasCode && showSubzones && (
                            <tr className="dicr-subzone-row">
                              <td colSpan={7}>
                                <div className="dicr-subzones">
                                  <span className="dicr-sz-arrow">↳</span>
                                  {sz.map((c) => (
                                    <span key={c} className="dicr-sz-chip">
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="dicr-actions">
              <button
                type="button"
                className="dicr-save"
                onClick={save}
                disabled={saving || !dirty}
                title={dirty ? "Enregistrer le fichier" : "Aucune modification"}
              >
                <HiSave /> {saving ? "Enregistrement…" : "Enregistrer le fichier"}
              </button>
            </div>
          </div>

          {/* ── Étiquettes ─────────────────────────────────────────── */}
          <div className="dicr-card">
            <label className="dicr-label">Étiquettes (A8)</label>
            <div className="dicr-types">
              <button
                type="button"
                className={`dicr-type-btn ${codeType === "barcode" ? "active" : ""}`}
                onClick={() => setCodeType("barcode")}
              >
                ▐▍▐ Code-barres (Code128)
              </button>
              <button
                type="button"
                className={`dicr-type-btn ${codeType === "qr" ? "active" : ""}`}
                onClick={() => setCodeType("qr")}
              >
                ▣ QR code
              </button>
            </div>
            <div className="dicr-types">
              <button
                type="button"
                className={`dicr-type-btn ${avecSousZones ? "active" : ""}`}
                onClick={() => setAvecSousZones(true)}
                title="Une étiquette par sous-zone (codes avec suffixe _A, _B…)"
              >
                Par sous-zone (_A, _B…)
              </button>
              <button
                type="button"
                className={`dicr-type-btn ${!avecSousZones ? "active" : ""}`}
                onClick={() => setAvecSousZones(false)}
                title="Une étiquette par zone, code GISM1 seul (sans suffixe _A/_B)"
              >
                Par zone (sans suffixe)
              </button>
            </div>
            <div className="dicr-types">
              {["TOUS", "MAGASIN", "DOCK"].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`dicr-type-btn ${emplEtiquettes === v ? "active" : ""}`}
                  onClick={() => setEmplEtiquettes(v)}
                  title={
                    v === "TOUS"
                      ? "Imprimer les rayons des deux emplacements"
                      : `N'imprimer que les rayons de l'emplacement ${v}`
                  }
                >
                  {v === "TOUS" ? "Tous les emplacements" : v}
                </button>
              ))}
            </div>
            <span className="dicr-hint-inline">
              Génère {avecSousZones ? totalSousZonesEtiq : nbCodesEtiq}{" "}
              étiquette(s) à partir du fichier <strong>enregistré</strong>
              {avecSousZones
                ? " (une par sous-zone"
                : " (une par zone, code sans suffixe"}
              {emplEtiquettes !== "TOUS"
                ? `, emplacement ${emplEtiquettes}).`
                : ")."}
              {dirty ? " Enregistrez d'abord vos modifications." : ""}
            </span>

            <div className="dicr-actions">
              <button
                type="button"
                className="dicr-generate"
                onClick={generer}
                disabled={generating || dirty || nbCodesEtiq === 0}
                title={
                  nbCodesEtiq === 0 && nbCodes > 0
                    ? `Aucun rayon pour l'emplacement ${emplEtiquettes}`
                    : undefined
                }
              >
                <HiTag /> {generating ? "Génération…" : "Générer les étiquettes (A8)"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDictionnaireRayonsScreen;
